import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Card, CategoryChip, ChangeText, EmptyState, ImpactBadge, LiveNumber, Skeleton } from '../ui'
import { api } from '../lib/api'
import { useConfig } from '../lib/config'
import { useStream } from '../lib/useStream'
import { usePolled } from '../lib/usePolled'
import { useLiveStocks } from '../lib/liveMarket'
import { usePageTitle } from '../lib/usePageTitle'
import { formatNumber, formatTimeIST } from '../lib/format'
import type { Filing, FilingStats, Page } from '../lib/types'
import { RequireAuth } from '../app/RequireAuth'
import { RiskRadarCard } from './RiskRadar'
import { WatchlistSidebar } from '../features/WatchlistSidebar'
import styles from './DashboardPage.module.css'

interface SectorRow { sector: string; filings: number; high_impact: number; change_pct: number | null }
interface CorporateAction { id: number; symbol: string; name: string | null; action_type: string; description: string | null; ex_date: string | null; record_date: string | null; held: boolean }
interface Delivery { id: number; channel: string; created_at: string; symbol: string | null; headline: string | null; category: string | null }
interface PortfolioSummary { holdings_count: number; invested: number; current_value: number | null; day_pnl?: number | null; priced_cost?: number; priced?: number; prices_connected: boolean }
interface Breadth { connected: boolean; universe?: string; advances: number; declines: number; unchanged: number; week52_highs: number; week52_lows: number }
interface Mover { symbol: string; name: string | null; ltp: number; change: number | null; change_pct: number | null; filing_category: string | null }
interface Movers { connected: boolean; universe?: string; gainers: Mover[]; losers: Mover[] }

const NOT_CONNECTED = 'Market data not connected'

export function DashboardPage() {
  usePageTitle('Dashboard')
  return (
    <RequireAuth what="your dashboard">
      <Dashboard />
    </RequireAuth>
  )
}

function Dashboard() {
  const cfg = useConfig()
  const [stats, setStats] = useState<FilingStats | null>(null)
  const [sectors, setSectors] = useState<SectorRow[] | null>(null)
  const [impact, setImpact] = useState<Filing[] | null>(null)
  const [actions, setActions] = useState<{ connected: boolean; data: CorporateAction[] } | null>(null)
  const [deliveries, setDeliveries] = useState<Delivery[] | null>(null)
  const [portfolio, setPortfolio] = useState<PortfolioSummary | null>(null)
  const [fresh, setFresh] = useState<Set<number>>(new Set())
  const breadth = usePolled<Breadth>(cfg.prices_enabled ? '/market/breadth' : null, 10000)
  const movers = usePolled<Movers>(cfg.prices_enabled ? '/market/movers' : null, 10000)
  const live = !!breadth?.connected

  const loadImpact = useCallback(() =>
    api<Page<Filing>>('/filings?min_impact=70&date=today&limit=6').then(r => setImpact(r.data)).catch(() => setImpact([])), [])

  useEffect(() => {
    void loadImpact()
    api<FilingStats>('/filings/stats').then(setStats).catch(() => undefined)
    api<{ data: SectorRow[] }>('/sectors/today').then(r => setSectors(r.data)).catch(() => setSectors([]))
    api<{ connected: boolean; data: CorporateAction[] }>('/corporate-actions?days=14').then(setActions).catch(() => setActions({ connected: false, data: [] }))
    api<{ data: Delivery[] }>('/alerts/deliveries').then(r => setDeliveries(r.data)).catch(() => setDeliveries([]))
    const loadPortfolio = () => { if (document.visibilityState === 'visible') api<PortfolioSummary>('/portfolio/summary').then(setPortfolio).catch(() => undefined) }
    loadPortfolio()
    // Holdings value/P&L come from the live snapshot server-side; refresh them every 15 s.
    const t = setInterval(loadPortfolio, 15000)
    return () => clearInterval(t)
  }, [loadImpact])

  // Live high-impact list + watchlist badges.
  useStream(['filing.created', 'filing.updated'], e => {
    const id = (e.data as { id?: number } | null)?.id
    if (!id) return
    api<{ data: Filing }>(`/filings/${id}`).then(({ data }) => {
      if ((data.impact_score ?? 0) >= 70) {
        setImpact(list => [data, ...(list ?? []).filter(x => x.id !== id)].slice(0, 6))
        setFresh(s => new Set(s).add(id))
        setTimeout(() => setFresh(s => { const n = new Set(s); n.delete(id); return n }), 2200)
        api<FilingStats>('/filings/stats').then(setStats).catch(() => undefined)
      }
    }).catch(() => undefined)
  })

  return (
    <div className={styles.grid}>
      <div className={styles.watch}>
        <WatchlistSidebar />
      </div>

      <div className={styles.center}>
        <h1 className="sr-only">Market dashboard</h1>
        <Card title="Market breadth · NSE" action={live ? breadth?.universe : cfg.prices_enabled && breadth === undefined ? undefined : NOT_CONNECTED}>
          {!live && <p className={styles.notice}>Advances, declines and 52-week highs/lows need the market-data feed.</p>}
          {live && breadth && <BreadthBar b={breadth} />}
          <dl className={styles.breadthStats}>
            <div><dt>52-week highs</dt><dd className={live ? styles.up : styles.muted}>{live ? formatNumber(breadth!.week52_highs, 0) : '—'}</dd></div>
            <div><dt>52-week lows</dt><dd className={live ? styles.down : styles.muted}>{live ? formatNumber(breadth!.week52_lows, 0) : '—'}</dd></div>
            <div><dt>Filings today</dt><dd>{stats ? formatNumber(stats.filings_today, 0) : '…'}</dd></div>
            <div><dt>High-impact filings</dt><dd className={styles.event}>{stats ? formatNumber(stats.high_impact_today, 0) : '…'}</dd></div>
          </dl>
        </Card>

        <div className={styles.two}>
          <Card title="Top gainers · top 500" variant="flush">
            <MoverList movers={movers} side="gainers" />
          </Card>
          <Card title="Top losers · top 500" variant="flush">
            <MoverList movers={movers} side="losers" />
          </Card>
        </div>

        <Card title="Sector activity" action={live ? 'Filings today · average % move (top 500)' : 'Filings today (non-routine) · % moves: not connected'}>
          {sectors === null ? <Skeleton variant="row" lines={2} label="Loading sectors" />
            : sectors.length === 0 ? <EmptyState title="No sector filings yet today" compact />
            : (
              <ul className={styles.sectors}>
                {sectors.map(s => (
                  <li key={s.sector} className={styles.tile}>
                    <span className={styles.tileName}>{s.sector}</span>
                    {s.change_pct !== null && <ChangeText percent={s.change_pct} size="sm" />}
                    <span className={styles.tileCount}>{formatNumber(s.filings, 0)}</span>
                    <span className={styles.tileSub}>filings · {s.high_impact} high impact</span>
                  </li>
                ))}
              </ul>
            )}
        </Card>

        <Card title="High-impact filings today" variant="flush" action={<Link to="/announcements?min=70&date=today">All announcements →</Link>}>
          {impact === null ? <div className={styles.pad}><Skeleton variant="row" lines={4} label="Loading filings" /></div>
            : impact.length === 0 ? <EmptyState title="No filings with impact 70+ yet today" compact />
            : (
              <ul className={styles.impactList}>
                {impact.map(r => (
                  <li key={r.id} className={`${styles.impactRow} ${fresh.has(r.id) ? styles.fresh : ''}`}>
                    <span className={styles.time}>{formatTimeIST(r.filed_at)}</span>
                    <span className={styles.who}>
                      <Link to={`/stock/${encodeURIComponent(r.symbol)}`} className={styles.sym}>{r.symbol}</Link>
                      {r.category ? <CategoryChip category={r.category} size="sm" /> : null}
                    </span>
                    <span className={styles.what}>
                      <span className={styles.title}>{r.headline}</span>
                      <span className={styles.sub}>{r.ai_summary_en ?? r.exchange_category}</span>
                    </span>
                    <ImpactBadge score={r.impact_score} size="sm" />
                  </li>
                ))}
              </ul>
            )}
        </Card>
      </div>

      <div className={styles.rail}>
        <Card title={portfolio?.holdings_count ? `Your holdings (${portfolio.holdings_count})` : 'Your holdings'} variant="navy"
          action={portfolio?.holdings_count ? <Link to="/portfolio">Details</Link> : undefined}>
          {portfolio === null ? <Skeleton lines={2} label="Loading holdings" />
            : portfolio.holdings_count === 0 ? (
              <div className={styles.cta}>
                <p>Import your holdings to see filings, corporate actions and Risk Radar flags for the stocks you own.</p>
                <Link to="/portfolio" className={styles.ctaBtn}>Import holdings</Link>
              </div>
            ) : (
              portfolio.current_value !== null ? (
                <div className={styles.pf}>
                  <span className={styles.pfValue}>₹{formatNumber(portfolio.current_value, 0)}</span>
                  <span className={styles.navyText}>Today <ChangeText absolute={portfolio.day_pnl ?? 0} percent={portfolio.current_value - (portfolio.day_pnl ?? 0) ? ((portfolio.day_pnl ?? 0) / (portfolio.current_value - (portfolio.day_pnl ?? 0))) * 100 : 0} decimals={0} size="sm" onDark /></span>
                  {portfolio.priced_cost ? <span className={styles.navyText}>Overall <ChangeText absolute={portfolio.current_value - portfolio.priced_cost} percent={((portfolio.current_value - portfolio.priced_cost) / portfolio.priced_cost) * 100} decimals={0} size="sm" onDark /></span> : null}
                  {portfolio.priced !== undefined && portfolio.priced < portfolio.holdings_count && <span className={styles.navyText}>{portfolio.holdings_count - portfolio.priced} holding(s) without a live price</span>}
                </div>
              ) : <p className={styles.navyText}>{portfolio.prices_connected ? 'No live prices for your holdings yet.' : 'Current value and P&L: market data not connected.'}</p>
            )}
        </Card>

        <RiskRadarCard scope="mine" days={30} limit={5} title="Risk Radar" emptyHint={portfolio?.holdings_count ? 'No flagged filings for your stocks in the last 30 days.' : 'Flags for your holdings and watchlists appear here: auditor resignations, rating downgrades, pledge increases, CEO/CFO exits, delayed results and penalties.'} />

        <Card title="Corporate actions ahead" variant="flush" action={<Link to="/calendar">Calendar</Link>}>
          {actions === null ? <div className={styles.pad}><Skeleton variant="row" lines={3} label="Loading actions" /></div>
            : !actions.connected ? <EmptyState kind="not-connected" title="Data not connected" compact>Corporate-actions feed arrives with the calendar.</EmptyState>
            : actions.data.length === 0 ? <EmptyState title="No actions in the next 14 days" compact />
            : (
              <ul className={styles.actionList}>
                {actions.data.slice(0, 6).map(a => {
                  const d = new Date(`${a.ex_date ?? a.record_date}T00:00:00Z`)
                  return (
                    <li key={a.id} className={styles.actionRow}>
                      <span className={styles.date}><strong>{d.getUTCDate()}</strong><span>{d.toLocaleString('en-IN', { month: 'short', timeZone: 'UTC' }).toUpperCase()}</span></span>
                      <span className={styles.what}><span className={styles.title}>{a.symbol}{a.held ? ' · holding' : ''}</span><span className={styles.sub}>{a.description ?? a.action_type}</span></span>
                    </li>
                  )
                })}
              </ul>
            )}
        </Card>

        <Card title="Alerts sent today" variant="flush" action="via Telegram & email">
          {deliveries === null ? <div className={styles.pad}><Skeleton variant="row" lines={2} label="Loading alerts" /></div>
            : deliveries.length === 0 ? <EmptyState title="No alerts sent to you today" compact action={<Link to="/alerts">Set up alerts</Link>} />
            : (
              <ul className={styles.impactList}>
                {deliveries.map(d => (
                  <li key={d.id} className={styles.alertRow}>
                    <span className={styles.time}>{formatTimeIST(d.created_at)} · {d.symbol}</span>
                    <span className={styles.sub}>{d.headline}</span>
                  </li>
                ))}
              </ul>
            )}
        </Card>
      </div>
    </div>
  )
}

function BreadthBar({ b }: { b: Breadth }) {
  const total = b.advances + b.declines + b.unchanged || 1
  return (
    <div className={styles.breadth}>
      <div className={styles.breadthBar} role="img" aria-label={`${b.advances} advancing, ${b.declines} declining, ${b.unchanged} unchanged`}>
        <span className={styles.barUp} style={{ width: `${(b.advances / total) * 100}%` }} />
        <span className={styles.barFlat} style={{ width: `${(b.unchanged / total) * 100}%` }} />
        <span className={styles.barDown} style={{ width: `${(b.declines / total) * 100}%` }} />
      </div>
      <div className={styles.breadthLegend}>
        <span className={styles.up}>▲ {formatNumber(b.advances, 0)} advancing</span>
        <span className={styles.muted}>{formatNumber(b.unchanged, 0)} unchanged</span>
        <span className={styles.down}>▼ {formatNumber(b.declines, 0)} declining</span>
      </div>
    </div>
  )
}

function MoverList({ movers, side }: { movers: Movers | undefined; side: 'gainers' | 'losers' }) {
  const live = useLiveStocks()
  if (movers === undefined) return <div className={styles.pad}><Skeleton variant="row" lines={4} label="Loading movers" /></div>
  if (!movers.connected) return <EmptyState kind="not-connected" title={NOT_CONNECTED} compact />
  const rows = movers[side]
  if (!rows.length) return <EmptyState title={side === 'gainers' ? 'No gainers yet' : 'No losers yet'} compact />
  return (
    <ul className={styles.movers}>
      {rows.map(m => (
        <li key={m.symbol} className={styles.moverRow}>
          <span className={styles.moverWho}>
            <Link to={`/stock/${encodeURIComponent(m.symbol)}`} className={styles.sym}>{m.symbol}</Link>
            {m.filing_category ? <CategoryChip category={m.filing_category} size="sm" /> : null}
          </span>
          <LiveNumber className={styles.moverPx} value={live(m.symbol)?.ltp ?? m.ltp} />
          <ChangeText percent={live(m.symbol)?.change_pct ?? m.change_pct} size="sm" weight="bold" />
        </li>
      ))}
    </ul>
  )
}
